/**
 * Session Expired Context
 * Manages global state for showing session expired modal
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import SessionExpiredModal from '../components/UI/SessionExpiredModal';

interface SessionExpiredContextType {
  showSessionExpired: (message?: string) => void;
  hideSessionExpired: () => void;
}

const SessionExpiredContext = createContext<SessionExpiredContextType | undefined>(undefined);

export const SessionExpiredProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState<string>('Your session has expired for security reasons. Please log in again to continue.');

  const showSessionExpired = useCallback((customMessage?: string) => {
    if (customMessage) {
      setMessage(customMessage);
    }
    setIsOpen(true);
  }, []);

  const hideSessionExpired = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <SessionExpiredContext.Provider value={{ showSessionExpired, hideSessionExpired }}>
      {children}
      <SessionExpiredModal 
        isOpen={isOpen} 
        onClose={hideSessionExpired}
        message={message}
      />
    </SessionExpiredContext.Provider>
  );
};

export const useSessionExpired = () => {
  const context = useContext(SessionExpiredContext);
  if (!context) {
    throw new Error('useSessionExpired must be used within SessionExpiredProvider');
  }
  return context;
};

